// src/journey/FigureMatchJourneyView.jsx
import React, { useState, useCallback, useEffect } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import useFigureMatchGameLogic from '../hooks/useFigureMatchGameLogic';
import styles from '../views/FigureMatchGame.module.css';
import { usePreloadedData } from "../context/PreloadContext";

const FigureMatchJourneyView = ({ gameLength = 2, onComplete }) => {
    const [showInstructions, setShowInstructions] = useState(true);
    const [done, setDone] = useState(false);
    // Renamed for clarity: we'll show an "End Game Options" modal first
    const [showEndGameOptions, setShowEndGameOptions] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [figuresInCurrentGame, setFiguresInCurrentGame] = useState([]);

    const { preloadedData } = usePreloadedData();

    const {
        loading,
        error,
        cards,
        matchedPairs,
        moves,
        gameOver,
        shakingCards,
        currentFigureBiography,
        showBiographyModal,
        handleCardClick,
        resetGame,
        closeBiographyModal,
    } = useFigureMatchGameLogic({
        navigate: null,
        preloadedData,
        pairsToMatch: gameLength,
        isJourneyMode: true,
    });

    const handleProceedToGame = useCallback(() => {
        setShowInstructions(false);
    }, []);

    // When game finishes (all pairs matched)
    useEffect(() => {
        if (gameOver && !done) {
            const totalPairs = new Set(cards.map(c => c.pairId)).size;
            const allMatched = matchedPairs.length === totalPairs;

            if (allMatched) {
                setDone(true);
                // Gather all unique figures for review modal
                const uniqueFigures = [];
                const seen = new Set();

                cards.forEach(card => {
                    if (card.type === 'name' && !seen.has(card.pairId)) {
                        seen.add(card.pairId);
                        uniqueFigures.push({
                            pairId: card.pairId,
                            content: card.content,
                            biography: card.biography,
                            reference: card.reference,
                        });
                    }
                });
                setFiguresInCurrentGame(uniqueFigures);
            }
        }
    }, [gameOver, matchedPairs.length, cards, done]);

    // After biography modal closes AND game is done, show End Game Options
    useEffect(() => {
        // Only show end game options if the game is done, the biography modal is closed, and we haven't already shown the options.
        if (done && !showBiographyModal && !showEndGameOptions && !showReviewModal) {
            const timer = setTimeout(() => setShowEndGameOptions(true), 400);
            return () => clearTimeout(timer);
        }
    }, [done, showBiographyModal, showEndGameOptions, showReviewModal]);

    // Handles proceeding directly to the next activity
    const handleContinueJourney = () => {
        setShowEndGameOptions(false);
        setShowReviewModal(false);
        if (typeof onComplete === 'function') {
            onComplete('figurematch');
        }
    };

    // Handles opening the review modal from the options modal
    const handleReviewFigures = () => {
        setShowEndGameOptions(false); // Hide the options modal
        setShowReviewModal(true); // Show the review modal
    };

    // Handles closing the review modal (which always leads to continuing the journey)
    const handleCloseReview = () => {
        handleContinueJourney();
    };

    const backgroundStyle = {
        backgroundImage: `url("/images/figure.jpg")`,
    };

    if (loading) {
        return (
            <div className={styles.gameContainer} style={backgroundStyle}>
                <LoadingSpinner message="Loading Islamic Figures Match..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.gameContainer} style={backgroundStyle}>
                <p className={styles.errorMessage}>Error: {error}</p>
                <button onClick={resetGame} className={styles.button}>Retry</button>
            </div>
        );
    }

    // If the game is over and we're not showing instructions,
    // we should only render the modals.
    // The main game content (cards) will be hidden by the modal overlay.
    // This prevents the brief flash of the 'gameOver' message/section.
    if (!showInstructions && (showEndGameOptions || showReviewModal)) {
        // We will skip rendering the main game elements and proceed directly to modals.
    }

    const nameCards = cards.filter(card => card.type === 'name');
    const attributeCards = cards.filter(card => card.type === 'attribute');
    const use2PairLayout = gameLength === 2;

    return (
        <div className={styles.gameContainer} style={backgroundStyle}>
            <h2 className={styles.title}>🕌 Journey Activity: Islamic Figures Match</h2>

            {showInstructions ? (
                <div className={styles.instructionsPopup}>
                    <h3>Journey Mode Instructions:</h3>
                    <ul>
                        <li>Match Islamic figures with their key attributes</li>
                        <li>Click a Name card and an Attribute card to reveal matches</li>
                        <li>Complete all {gameLength} pairs to advance in your journey</li>
                        <li>No lives limit - focus on learning!</li>
                    </ul>
                    <button className={styles.button} onClick={handleProceedToGame}>
                        Start Activity
                    </button>
                </div>
            ) : (
                <>
                    {/* Only show game content if the game isn't over OR if an instruction/biography modal is open */}
                    {(!gameOver || showBiographyModal) && (
                        <>
                            <div className={styles.gameInfo}>
                                <p>Moves: {moves} | Pairs Matched: {matchedPairs.length} / {new Set(cards.map(c => c.pairId)).size}</p>
                            </div>

                            {/* Cards Grid/Container logic remains the same */}
                            {use2PairLayout ? (
                                <div className={styles.journeyCardsContainer}>
                                    {/* Name cards row */}
                                    <div className={styles.journeyCardsRow}>
                                        {nameCards.map((card) => (
                                            <div
                                                key={card.instanceId}
                                                className={`${styles.card} ${styles.journeyCard} ${card.isFlipped ? styles.flipped : ''} ${styles.nameCard} ${card.isMatched ? styles.matched : ''} ${shakingCards.includes(card.instanceId) ? styles.shaking : ''}`}
                                                onClick={() => handleCardClick(card)}
                                            >
                                                <div className={styles.cardInner}>
                                                    <div className={styles.cardFront}>
                                                        <p className={styles.cardText}>{card.content}</p>
                                                    </div>
                                                    <div className={styles.cardBack}>
                                                        <p className={styles.cardText}>{card.content}</p>
                                                        {card.reference && <p className={styles.cardReference}>{card.reference}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Attribute cards row */}
                                    <div className={styles.journeyCardsRow}>
                                        {attributeCards.map((card) => (
                                            <div
                                                key={card.instanceId}
                                                className={`${styles.card} ${styles.journeyCard} ${card.isFlipped ? styles.flipped : ''} ${styles.attributeCard} ${card.isMatched ? styles.matched : ''} ${shakingCards.includes(card.instanceId) ? styles.shaking : ''}`}
                                                onClick={() => handleCardClick(card)}
                                            >
                                                <div className={styles.cardInner}>
                                                    <div className={styles.cardFront}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" className={styles.cardIcon}>
                                                            <path fill="currentColor" d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32h82.7L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3V192c0 17.7 14.3 32 32 32s32-14.3 32-32V32c0-17.7-14.3-32-32-32H320zM80 64C35.8 64 0 99.8 0 144V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V352H288c-17.7 0-32-14.3-32-32s14.3-32 32-32h96V144c0-35.3-28.7-64-64-64H80z"/>
                                                        </svg>
                                                    </div>
                                                    <div className={styles.cardBack}>
                                                        <p className={styles.cardText}>{card.content}</p>
                                                        {card.reference && <p className={styles.cardReference}>{card.reference}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.cardsGrid}>
                                    {cards.map((card) => (
                                        <div
                                            key={card.instanceId}
                                            className={`${styles.card} ${card.isFlipped ? styles.flipped : ''} ${card.type === 'name' ? styles.nameCard : styles.attributeCard} ${card.isMatched ? styles.matched : ''} ${shakingCards.includes(card.instanceId) ? styles.shaking : ''}`}
                                            onClick={() => handleCardClick(card)}
                                        >
                                            <div className={styles.cardInner}>
                                                <div className={styles.cardFront}>
                                                    {card.type === 'name' ? (
                                                        <p className={styles.cardText}>{card.content}</p>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" className={styles.cardIcon}>
                                                            <path fill="currentColor" d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32h82.7L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3V192c0 17.7 14.3 32 32 32s32-14.3 32-32V32c0-17.7-14.3-32-32-32H320zM80 64C35.8 64 0 99.8 0 144V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V352H288c-17.7 0-32-14.3-32-32s14.3-32 32-32h96V144c0-35.3-28.7-64-64-64H80z"/>
                                                        </svg>
                                                    )}
                                                </div>
                                                <div className={styles.cardBack}>
                                                    <p className={styles.cardText}>{card.content}</p>
                                                    {card.reference && <p className={styles.cardReference}>{card.reference}</p>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Biography Modal */}
            {showBiographyModal && currentFigureBiography && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <h3>{currentFigureBiography.content}</h3>
                        <p className={styles.modalBiography}>{currentFigureBiography.biography}</p>
                        <button onClick={closeBiographyModal} className={styles.button}>Close</button>
                    </div>
                </div>
            )}

            {/* NEW End Game Options Modal */}
            {showEndGameOptions && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <p className={styles.finalScoreMessage}>Excellent! All pairs matched! 🎉</p>
                        <h3>What would you like to do next?</h3>
                        <div className={styles.modalActions}>
                            <button onClick={handleReviewFigures} className={`${styles.button} ${styles.primary}`}>
                                Review Figures
                            </button>
                            <button onClick={handleContinueJourney} className={styles.button}>
                                Continue Journey
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Post-Game Review Modal */}
            {showReviewModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.reviewModalContent}>
                        <h3>Review Figures</h3>
                        <div className={styles.reviewFiguresList}>
                            {figuresInCurrentGame.map(figure => (
                                <div key={figure.pairId} className={styles.reviewFigureItem}>
                                    <h4>{figure.content}</h4>
                                    <p>{figure.biography}</p>
                                    {figure.reference && (
                                        <p className={styles.reviewFigureReference}>{figure.reference}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button onClick={handleCloseReview} className={styles.button}>Continue Journey</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FigureMatchJourneyView;